/**
 * Serialization functions for converting JSX dashboard trees to JSON.
 *
 * This module provides the public API for rendering JSX dashboards to
 * DashboardGridData JSON format. It uses the reconciler to build an
 * internal node tree, then transforms it to the expected JSON structure.
 *
 * @example
 * ```tsx
 * import { renderDashboard } from '#core/components/dashboard/reconciler';
 *
 * const json = renderDashboard(
 *   <dashboard-grid rows={3} columns={12}>
 *     <grid-item row={1} column={1} columnSpan={6}>
 *       <dashboard-kpi value={42} label="Metric" />
 *     </grid-item>
 *   </dashboard-grid>
 * );
 * // json is a valid DashboardGridData object
 * ```
 *
 * @module reconciler/serialize
 */
import type { ReactElement } from 'react';

import type { GridItem, GridLayout } from '../schema.ts';
import { logGridCorrections, validateAndCorrectGrid } from '../validation.ts';
import { DashboardRenderError } from './errors.ts';
import { getRegisteredElementNames, renderToTree } from './reconciler.ts';
import {
  type ComponentNode,
  type GridItemNode,
  type GridNode,
  isComponentNode,
  isGridItemNode,
  isGridNode,
  type Node,
} from './types.ts';

/**
 * Output format for JSX-to-JSON serialization.
 * Uses schema types as single source of truth.
 * FIXME: conflicts with ../schema export
 */
export interface DashboardGridData {
  layout: GridLayout;
  items: DashboardGridItem[];
  title?: string;
}

/**
 * Grid item type for reconciler output.
 * Extends schema's GridItem with looser component typing for reconciler flexibility.
 */
export interface DashboardGridItem extends Omit<GridItem, 'component'> {
  component: Record<string, unknown>;
}

function validateGridItemPosition(item: GridItemNode): void {
  if (item.position.row === 0 || item.position.column === 0) {
    throw new Error(
      'Grid item missing required position props: row and column must be specified (1-indexed). ' +
        'Example: <grid-item row={1} column={1}>...',
    );
  }
}

function serializeComponent(node: ComponentNode): Record<string, unknown> {
  return node.props;
}

function serializeGridItem(node: GridItemNode): DashboardGridItem {
  validateGridItemPosition(node);

  if (node.child === null) {
    throw new Error(
      'Grid item has no component child. Each grid-item must contain exactly one component. ' +
        'Example: <grid-item row={1} column={1}><dashboard-kpi .../></grid-item>',
    );
  }

  const item: DashboardGridItem = {
    position: { ...node.position },
    component: serializeComponent(node.child),
  };

  if (node.caption !== undefined) {
    item.caption = node.caption;
  }

  if (node.captionPosition !== undefined) {
    item.captionPosition = node.captionPosition;
  }

  if (node.layer !== undefined) {
    item.layer = node.layer;
  }

  return item;
}

function validateGridChildren(children: Node[]): void {
  for (const child of children) {
    if (child.kind === 'text') {
      continue;
    }

    // Skip fragment nodes (shouldn't happen normally)
    if (child.kind === 'fragment') {
      continue;
    }

    if (!isGridItemNode(child) && isComponentNode(child)) {
      throw new Error(
        `Component "${child.kind}" found directly inside dashboard-grid. ` +
          'Components must be wrapped in a grid-item element. ' +
          'Example: <dashboard-grid><grid-item row={1} column={1}><dashboard-kpi .../></grid-item></dashboard-grid>',
      );
    }

    if (!isGridItemNode(child)) {
      throw new Error(`Invalid child type "${child.kind}" inside dashboard-grid. Only grid-item elements are allowed.`);
    }
  }
}

function serializeGrid(node: GridNode): DashboardGridData {
  const gridItemChildren = node.children.filter(isGridItemNode);

  validateGridChildren(node.children);

  const serializedItems = gridItemChildren.map(serializeGridItem);

  const result = validateAndCorrectGrid(serializedItems, node.layout);
  if (result.corrections.length > 0) {
    logGridCorrections(result.corrections);
  }

  const data: DashboardGridData = {
    layout: result.layout,
    items: result.items,
  };

  if (node.title !== undefined) {
    data.title = node.title;
  }

  return data;
}

/**
 * Renders a JSX dashboard tree to DashboardGridData JSON.
 *
 * This is the main public API for converting JSX dashboards to JSON.
 * The JSX tree must have a dashboard-grid element at the root.
 *
 * @param jsx - The JSX element tree to render
 * @returns DashboardGridData JSON object
 * @throws DashboardRenderError if the JSX structure is invalid or conversion fails
 *
 * @example
 * ```tsx
 * const json = renderDashboard(
 *   <dashboard-grid rows={3} columns={12} title="My Dashboard">
 *     <grid-item row={1} column={1} columnSpan={4}>
 *       <dashboard-kpi value={42} label="Metric" />
 *     </grid-item>
 *     <grid-item row={1} column={5} columnSpan={8}>
 *       <dashboard-chart chartType="bar" data={[]} xAxis="x" yAxis="y" />
 *     </grid-item>
 *   </dashboard-grid>
 * );
 * ```
 */
export function renderDashboard(jsx: ReactElement): DashboardGridData {
  const elementType = typeof jsx.type === 'string' ? jsx.type : (jsx.type?.name ?? 'unknown');

  try {
    const tree = renderToTree(jsx);

    if (!isGridNode(tree)) {
      const registeredElements = getRegisteredElementNames().join(', ');
      throw new DashboardRenderError(
        'Dashboard must have a dashboard-grid element at the root. ' +
          `Found: "${tree.kind}" instead. ` +
          `Available elements: ${registeredElements}`,
        elementType,
      );
    }

    return serializeGrid(tree);
  } catch (error) {
    if (error instanceof DashboardRenderError) {
      throw error;
    }
    throw new DashboardRenderError(
      `Failed to render dashboard JSX: ${error instanceof Error ? error.message : String(error)}`,
      elementType,
      error,
    );
  }
}
